import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  model,
  signal,
  WritableSignal,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { distinctUntilChanged } from 'rxjs';
import { InputWithChipFormFieldV2Component } from '../../../../shared/components/input-with-chip-form-field-v2/input-with-chip-form-field-v2.component';
import {
  ModalConfirmComponent,
  ModalConfirmProps,
  ModalConfirmType,
} from '../../../../shared/components/modal-confirm/modal-confirm.component';
import {
  SnackInfoComponent,
  SnackInfoProps,
  SnackInfoType,
} from '../../../../shared/components/snack-info/snack-info.component';
import { TextareaWithSpeechToTextFormFieldComponent } from '../../../../shared/components/textarea-with-speech-to-text-form-field/textarea-with-speech-to-text-form-field.component';
import { UploadArchivosComponent } from '../../../../shared/components/upload-archivos/upload-archivos.component';
import { AppPermissions, ValidRoles } from '../../../../shared/enums';
import { UploadArchivos } from '../../../../shared/interfaces/cargaArchivo.interface';
import { ExamenesPaciente, IDataExamn } from '../../../../shared/interfaces/examenes.interface';
import { GuardadoExamenesService } from '../../../../shared/services/guardado-examenes.service';
import { TabExamenesService, UploadedFile } from '../../../../shared/services/tab-examenes.service';
import { FichaClinicaStore } from '../../../../shared/stores/ficha-clinica/ficha-clinica.store';
import { HasAccessDirective } from '../../../../shared/directives/has-access/has-access.directive';
import { AuthenticationService } from '../../../services/authentication.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FinishAppointmentButtonComponent } from '../../../common/components/finish-appointment-button/finish-appointment-button.component';
import { fichaClinicaFinalizada } from '../../../../shared/helpers/ficha-clinica.helper';

interface ExamenesForm {
  examenLaboratorio: FormControl<Array<string> | null>;
  examenImagen: FormControl<Array<string> | null>;
  fileExam: FormControl<Array<ExamenesPaciente> | null>;
  diagnosticoExamen: FormControl<string | null>;
}

interface ExamenesFormValues {
  examenLaboratorio: Array<string> | null;
  examenImagen: Array<string> | null;
  fileExam: Array<ExamenesPaciente> | null;
  diagnosticoExamen: string | null;
}

@Component({
  selector: 'soph-examenes',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    UploadArchivosComponent,
    TextareaWithSpeechToTextFormFieldComponent,
    InputWithChipFormFieldV2Component,
    HasAccessDirective,
    MatProgressSpinnerModule,
    FinishAppointmentButtonComponent,
  ],
  templateUrl: './examenes.component.html',
  styleUrl: './examenes.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExamenesComponent {
  public readonly formReady = model(false);
  readonly dialog = inject(MatDialog);
  readonly snackBar = inject(MatSnackBar);
  private readonly auth: AuthenticationService = inject(AuthenticationService);

  protected laboratorioTypeExam: string | null = null;
  protected imagenesTypeExam: string | null = null;

  protected examsLaboratorio = signal<Array<IDataExamn>>([]);
  protected examsImagen = signal<Array<IDataExamn>>([]);

  protected form: FormGroup<ExamenesForm>;

  protected readonly errorMessage: WritableSignal<{ [key: string]: string }> = signal({});

  private examenes = signal<ExamenesFormValues | null>(null);

  protected fileName: string = '';
  protected file: UploadArchivos[] = [];
  protected hasDocumentUrl: boolean = false;
  protected documentUrl: string = '';
  protected isUploaderDisabled: boolean = false;

  protected readonly hasChanges = signal<boolean>(false);

  protected readonly examenesExistentes: WritableSignal<UploadedFile[]> = signal([]);

  protected readonly isSavingData = signal(false);
  protected readonly isAppointmentFinished = signal(false);

  constructor(
    private readonly _fb: FormBuilder,
    private readonly _fichaClinicaStore: FichaClinicaStore,
    private readonly _tabExamenesService: TabExamenesService,
    private readonly _guardadoExamenesService: GuardadoExamenesService
  ) {
    this.form = this._fb.group({
      examenImagen: new FormControl<Array<string> | null>([], Validators.compose([])),
      examenLaboratorio: new FormControl<Array<string> | null>([], Validators.compose([])),
      fileExam: new FormControl<Array<ExamenesPaciente> | null>([], Validators.compose([])),
      diagnosticoExamen: new FormControl<string | null>(
        null,
        Validators.compose([Validators.maxLength(1000)])
      ),
    });

    this._fichaClinicaStore?.state$.pipe(distinctUntilChanged()).subscribe((values) => {
      const isAppointmentFinished = fichaClinicaFinalizada(values.estado || '');
      this.isUploaderDisabled = isAppointmentFinished;
      isAppointmentFinished ? this.form.disable() : this.form.enable();
      this.isAppointmentFinished.set(isAppointmentFinished);
    });

    this.form.valueChanges.pipe(distinctUntilChanged()).subscribe((newValues) => {
      this.hasChanges.set(this.checkForChanges(newValues));
    });

    this._fichaClinicaStore.updateMedicalHistoryBySection('examenes', true);
    this.formReady.set(true);

    effect(
      () => {
        this._fichaClinicaStore.hasUnsavedChanges(this.hasChanges());
      },
      { allowSignalWrites: true }
    );
  }

  ngOnInit() {
    this.setTypesExams();

    if (!this.auth.hasRole(ValidRoles.MEDIC)) {
      this.form.disable();
      this.isUploaderDisabled = true;
    }
  }

  onExamenSelected(file: any): void {
    this.file = file;
  }

  public updateErrorMessage(fieldName: string) {
    const control: AbstractControl | null = this.form.get(fieldName);
    const currentErrorMessages = this.errorMessage();

    if (control?.hasError('required')) {
      currentErrorMessages[fieldName] = 'Este campo es obligatorio';
    } else {
      currentErrorMessages[fieldName] = '';
    }

    this.errorMessage.set({ ...currentErrorMessages });
  }

  public isRequired(fieldName: string) {
    const control: AbstractControl | null = this.form.get(fieldName);
    return control?.hasError('required') ?? false;
  }

  public isInvalid(fieldName: string) {
    const control: AbstractControl | null = this.form.get(fieldName);
    return control?.invalid ?? false;
  }

  public isBtnGuardarDisabled(): boolean {
    return this.form.invalid || !this.hasChanges();
  }

  private checkForChanges(newValues: any): boolean {
    if (this.examenes()) {
      return JSON.stringify(newValues) !== JSON.stringify(this.examenes());
    } else {
      const defaultValues = {
        examenImagen: [],
        examenLaboratorio: [],
        fileExam: [],
        diagnosticoExamen: null,
      };

      return JSON.stringify(defaultValues) !== JSON.stringify(newValues);
    }
  }

  protected openRevertChangesConfirmation() {
    const dialogRef = this.dialog.open(ModalConfirmComponent, {
      autoFocus: false,
      panelClass: 'custom-confirmation-dialog',
      width: '452px',
      data: {
        type: ModalConfirmType.WARNING,
        description: '¿Confirmas que deseas cancelar sin guardar?',
        config: {
          buttons: [
            { title: 'Volver', action: false },
            { title: 'Confirmar', action: true },
          ],
        },
      } as ModalConfirmProps,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.revertChanges();
      }
    });
  }

  public revertChanges() {
    if (this.examenes()) {
      this.form.patchValue({
        ...this.examenes(),
      });
    } else {
      this.form.reset();
    }
  }

  public onSave(form: FormGroup) {
    if (this.form.valid) {
      this.isSavingData.set(true);

      const reqGuardadoExam = {
        examenesImagen: this.form.get('examenImagen')?.value,
        examenesLaboratorio: this.form.get('examenLaboratorio')?.value,
        diagnosticoExamenes: this.form.get('diagnosticoExamen')?.value,
      };

      this.guardadoExamenes(this._fichaClinicaStore.values.idAgendamiento!, reqGuardadoExam);
    } else {
      Object.keys(this.form.controls).forEach((key) => {
        const control = this.form.get(key);
        control?.markAsTouched();
        control?.updateValueAndValidity();
      });

      this.snackBar.openFromComponent(SnackInfoComponent, {
        data: {
          message: 'Faltan completar campos requeridos.',
          type: SnackInfoType.WARNING,
        },
        duration: 3000,
      });
    }
  }

  private setTypesExams() {
    this._tabExamenesService.getTypesExams().subscribe((data) => {
      const laboratorio = data.find((exam: any) => exam.descripcion === 'Laboratorio');
      const imagenes = data.find((exam: any) => exam.descripcion === 'Imágenes');

      this.laboratorioTypeExam = laboratorio ? laboratorio.idTipoExamen : null;
      this.imagenesTypeExam = imagenes ? imagenes.idTipoExamen : null;
    });

    this.setExams();
  }

  private setExams() {
    this._tabExamenesService.getExamsByType().subscribe((data) => {
      const laboratorioExams = data
        .filter((examType: any) => examType.idTipoExamen === this.laboratorioTypeExam)
        .flatMap((examType: any) => examType.examenes);

      const imagenesExams = data
        .filter((examType: any) => examType.idTipoExamen === this.imagenesTypeExam)
        .flatMap((examType: any) => examType.examenes);

      this.examsLaboratorio.set(laboratorioExams);
      this.examsImagen.set(imagenesExams);
    });

    if (this._fichaClinicaStore.values.idAgendamiento) {
      this.getExamenesAtencion(this._fichaClinicaStore.values.idAgendamiento!);
    }
  }

  getExamenesAtencion(idAgendamiento: string) {
    this._tabExamenesService.getExamenesAtencion(idAgendamiento!).subscribe((response) => {
      if (response.success) {
        const data: ExamenesFormValues = {
          examenImagen: response.data.examenesAtencion.imagenes ?? [],
          examenLaboratorio: response.data.examenesAtencion.laboratorio ?? [],
          fileExam: response.data.examenesPaciente ?? [],
          diagnosticoExamen: response.data.diagnostico ?? null,
        };

        this.examenesExistentes.set(
          data.fileExam!.map((file: ExamenesPaciente) => ({
            fileName: file.nombreArchivo,
            destination: file.url,
          }))
        );

        this.examenes.set(data);

        this.form.patchValue(data);
      }
    });
  }

  guardadoExamenes(idAgendamiento: string, reqExam: any) {
    this._guardadoExamenesService.postGuardarExamenes(idAgendamiento, reqExam).subscribe({
      next: (_) => {
        this.examenes.set(this.form.value as ExamenesFormValues);

        this._fichaClinicaStore.updateMedicalHistoryBySection('examenes', true);
        this.formReady.set(true);

        this.snackBar.openFromComponent(SnackInfoComponent, {
          data: {
            message: 'Los cambios han sido guardados con éxito',
            type: SnackInfoType.SUCCESS,
          } as SnackInfoProps,
        });

        this.isSavingData.set(false);
      },
      error: (err) => {
        this.snackBar.openFromComponent(SnackInfoComponent, {
          data: {
            message: 'No pudimos guardar los cambios, por favor intenta nuevamente',
            type: SnackInfoType.ERROR,
          } as SnackInfoProps,
        });

        this.isSavingData.set(false);
      },
    });
  }

  protected readonly AppPermissions = AppPermissions;
}
